import { Flex, Loader, Select, Text } from "@mantine/core";
import { type JSX, useEffect, useState } from "react";
import { usePartner } from "@/sdk";
import { useFeatureFlag } from "@/shared-utils";
import { ChartHeader, PayLabel, SharedChart, StaticToolTip } from "./components";
import classes from "./PayDashboard.module.css";
import type { ScaleEntry } from "./types";
import { formatPayPeriod, trimScales } from "./utils";
import "@mantine/charts/styles.css";
import { useDashboardData } from "./calls/useDashboardData";
import { OtherPlansChart } from "./components/OtherPlansChart";
import { UpdatedTime } from "./components/UpdatedTime";

export function PayDashboard(): JSX.Element | null {
    const [showTooltip, setShowTooltip] = useState(true);
    const [chartMode, setChartMode] = useState("Sales");
    const [salePayouts, setSalePayouts] = useState<ScaleEntry[]>([]);
    const [saleInterval, setSaleInterval] = useState(0);
    const [surveyPayouts, setSurveyPayouts] = useState<ScaleEntry[]>([]);
    const [surveyInterval, setSurveyInterval] = useState(0);
    const { data, isLoading } = useDashboardData();
    const partner = usePartner();
    const { data: isOtherPlansEnabled } = useFeatureFlag("EWP-isOtherPlansEnabledDashboard", { partner });

    // biome-ignore lint/correctness/useExhaustiveDependencies: fix later
    useEffect(() => {
        if (data?.sellPayScale) {
            const { scale, factor } = trimScales(data.enrolls - data.churns, data.sellPayScale);
            setSalePayouts(scale);
            setSaleInterval(factor);
        }
        if (data?.servePayScale) {
            const { scale, factor } = trimScales(data.surveys, data.servePayScale);
            setSurveyPayouts(scale);
            setSurveyInterval(factor);
        }
    }, [data, chartMode]);
    if (isLoading) {
        return (
            <Flex direction="column" h={463} w="100%" justify="center" align="center">
                <Loader />
            </Flex>
        );
    }
    if (!data) {
        return (
            <Flex direction="column" h={463} w="100%" align="center" justify="center" p="md" fw={400}>
                We’ve run into an issue and can’t show your data right now. Try again later.
            </Flex>
        );
    }
    const currentSales = data.enrolls;
    const salesDropped = data.churns;
    const perfectSurveys = data.surveys;
    //TODO: get region information about expert
    const region = "PH";
    //TODO: remove this disable once we're pulling region information

    const payDenomination = region === "PH" ? "₱" : "$";
    const isSales = chartMode === "Sales";
    const isOther = chartMode === "Other";
    const netSales = currentSales - salesDropped;

    const salesPay = data.sellPay;
    const surveyPay = data.servePay;
    const leftSideTooltip = isSales
        ? netSales > (salePayouts.length - 1) / 3
        : perfectSurveys > (surveyPayouts.length - 1) / 3;
    const salesMetrics = [netSales, salesPay, data.sellRate];
    const surveyMetrics = [perfectSurveys, surveyPay, data.serveRate];

    const isSalesBroken = salesMetrics.includes(0) && salesMetrics.filter((it) => it > 0).length > 0;
    const isSurveyBroken = surveyMetrics.includes(0) && surveyMetrics.filter((it) => it !== 0).length > 0;
    const isChartBroken = isSales ? salePayouts.length <= 1 : surveyPayouts.length <= 1;
    const isMetricBroken = isSales ? isSalesBroken : isSurveyBroken; //if one of these has both 0 and non zero values, something is broken

    return (
        <Flex pr="xl" pl="xl" direction="column" mt="4xs" className={classes.rootForWidget}>
            <Flex className={classes.updateTimestamp} align="center">
                <UpdatedTime updatedTime={data.lastUpdated} />
            </Flex>
            <Flex className={classes.modalHeader} h="92px">
                <PayLabel
                    salesPay={!isSalesBroken ? salesPay : null}
                    surveyPay={!isSurveyBroken ? surveyPay : null}
                    otherPlansPay={500}
                    payDenomination={payDenomination}
                    otherPlansEnabled={isOtherPlansEnabled ?? false}
                    isMissingData={data.isMissingData}
                />
                <Flex direction="column" gap={4}>
                    {!isOtherPlansEnabled ? (
                        <Flex c="dark.2" fz="xs">
                            {`Pay period: ${data.payPeriodStart ? formatPayPeriod(data.payPeriodStart) : "pay period unknown"}`}
                        </Flex>
                    ) : (
                        <>
                            <Text fz="sm" c="dark.1">
                                Pay period
                            </Text>
                            <Select data={["Feb 1- Feb 14"]} />
                        </>
                    )}
                </Flex>
            </Flex>
            <ChartHeader value={chartMode} isOtherPlansEnabled={isOtherPlansEnabled ?? false} setValue={setChartMode} />
            <Flex className={classes.modalBody} h="100%" direction="column" justify="space-between">
                {!isChartBroken ? (
                    <>
                        {!isOther ? (
                            <>
                                <Flex className={classes.combinedArea} w="100%" h="100%">
                                    <SharedChart
                                        isMetricBroken={isMetricBroken}
                                        value={isSales ? currentSales : perfectSurveys}
                                        payList={isSales ? salePayouts : surveyPayouts}
                                        salesDropped={salesDropped}
                                        staticShowing={showTooltip}
                                        toggleTooltip={setShowTooltip}
                                        isSales={isSales}
                                        interval={isSales ? saleInterval : surveyInterval}
                                    />
                                    <StaticToolTip
                                        isSales={isSales}
                                        isBroken={isMetricBroken}
                                        leftSide={leftSideTooltip}
                                        stats={
                                            isSales
                                                ? {
                                                      value: netSales,
                                                      payout: salesPay,
                                                      penalties: salesDropped,
                                                      length: salePayouts.length,
                                                      perUnit: data.sellRate,
                                                  }
                                                : {
                                                      value: perfectSurveys,
                                                      payout: surveyPay,
                                                      length: surveyPayouts.length,
                                                      perUnit: data.serveRate,
                                                  }
                                        }
                                        show={showTooltip}
                                    />
                                </Flex>
                                <Flex justify="center" className={classes.xLabel}>
                                    {isSales ? "Net VHDP enrolls" : "Perfect Surveys"}
                                </Flex>
                            </>
                        ) : (
                            <OtherPlansChart />
                        )}
                    </>
                ) : (
                    <Flex justify="center">We’re unable to load the chart right now. Try again later.</Flex>
                )}
            </Flex>
        </Flex>
    );
}
